import type { CardStatus } from "../../types/domain";

export interface CardDraft {
  typeId: string;
  title: string;
  preview: string;
  status: CardStatus;
  time: string;
  dayKey: string;
  landingId: string | null;
}

export interface StoredCardDraft {
  version: 1;
  draft: CardDraft;
  baselineUpdatedAt: string | null;
}

const CARD_DRAFT_PREFIX = "lplanner:card-draft";

export function editCardDraftKey(ownerId: string, cardId: string) {
  return `${CARD_DRAFT_PREFIX}:${ownerId}:edit:${cardId}`;
}

export function createCardDraftKey(ownerId: string, launchId: string, dayKey: string) {
  return `${CARD_DRAFT_PREFIX}:${ownerId}:create:${launchId}:${dayKey}`;
}

export function loadStoredCardDraft(key: string): StoredCardDraft | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredCardDraft>;
    const draft = stored.draft as Partial<CardDraft> | undefined;
    if (
      stored.version !== 1 ||
      !draft ||
      typeof draft.typeId !== "string" ||
      typeof draft.title !== "string" ||
      typeof draft.preview !== "string" ||
      !["draft", "ready", "sent"].includes(draft.status ?? "") ||
      typeof draft.time !== "string" ||
      typeof draft.dayKey !== "string" ||
      !(draft.landingId === null || typeof draft.landingId === "string") ||
      !(stored.baselineUpdatedAt === null || typeof stored.baselineUpdatedAt === "string")
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    return stored as StoredCardDraft;
  } catch {
    return null;
  }
}

export function storeCardDraft(
  key: string,
  draft: CardDraft,
  baselineUpdatedAt: string | null,
) {
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ version: 1, draft, baselineUpdatedAt } satisfies StoredCardDraft),
    );
  } catch {
    // Local storage may be unavailable (for example in strict privacy mode).
  }
}

export function clearStoredCardDraft(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing else to clean up when local storage is unavailable.
  }
}

export function cardDraftsEqual(left: CardDraft, right: CardDraft) {
  return (
    left.typeId === right.typeId &&
    left.title === right.title &&
    left.preview === right.preview &&
    left.status === right.status &&
    left.time === right.time &&
    left.dayKey === right.dayKey &&
    left.landingId === right.landingId
  );
}
